import { unwrapResult } from '@reduxjs/toolkit';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import { useSnackBar } from 'src/hooks';
import { CreateTaxCertificateRequest } from 'src/redux/slices/purchase-order/types';
import {
  GetTaxCertificateListPayload,
  UpdateTaxCertificateRequest
} from 'src/redux/slices/purchase-order/tax-certificate/types';
import { formatTaxCertificateListParams } from 'src/utils/tax-certification/parameter-formatter';

const { actions: purchaseOrderActions } = slices.purchaseOrder;
const {
  actions: taxCertificateAction,
  selectors: taxCertificateSelector
} = slices.taxCertificate;

// this may expand in the future
export const useTaxCertificate = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackbar = useSnackBar();
  const [
    isCreateTaxCertificateLoading,
    setIsCreateTaxCertificateLoading
  ] = useState<boolean>(false);
  const [enablePDF, setEnablePDF] = useState<boolean>(false);

  const taxCertificateList = useAppSelector(
    taxCertificateSelector.selectTaxCertificateList
  );
  const taxCertificateListColumn = useAppSelector(
    taxCertificateSelector.selectTaxCertificateColumns
  );
  const taxCertificateListParams = useAppSelector(
    taxCertificateSelector.selectTaxCertificateParams
  );
  const taxCertificateListMeta = useAppSelector(
    taxCertificateSelector.selectTaxCertificateMeta
  );
  const isLoading = useAppSelector(taxCertificateSelector.selectIsLoading);
  const taxCertificateDetails = useAppSelector(
    taxCertificateSelector.selectTaxCertificateDetails
  );

  const handleCreateTaxCertificate = useCallback(
    async (payload: CreateTaxCertificateRequest) => {
      setIsCreateTaxCertificateLoading(true);
      const res = unwrapResult(
        await dispatch(purchaseOrderActions.createTaxCertificateThunk(payload))
      );

      const { data, message, errors } = res.originalData ?? {};

      if (res.success) {
        snackbar.show({
          severity: 'success',
          message: message ?? `Tax Certificate #${data} created successfully`
        });
        navigate(`/app/tax-certification/${data?.id}`);
      } else {
        snackbar.show({
          severity: 'error',
          message: errors
            ? Object.values(errors).join(', ')
            : message ??
              'Failed to create tax certificate. Try again later or contact support'
        });
      }

      setIsCreateTaxCertificateLoading(false);
      return res;
    },
    [dispatch, snackbar]
  );

  const handleUpdateTaxCertificate = useCallback(
    async (payload: UpdateTaxCertificateRequest) => {
      try {
        setIsCreateTaxCertificateLoading(true);
        const res = unwrapResult(
          await dispatch(
            taxCertificateAction.taxCertificateUpdateThunk(payload)
          )
        );
        const { message, errors } = res.originalData ?? {};

        if (res.success) {
          snackbar.show({
            severity: 'success',
            message: 'Tax Certificate update successfully'
          });
          setEnablePDF(true);
        } else {
          snackbar.show({
            severity: 'error',
            message: errors
              ? Object.values(errors).join(', ')
              : message ??
                'Failed to update tax certificate. Try again later or contact support'
          });
        }

        setIsCreateTaxCertificateLoading(false);
      } catch (e) {
        console.error(e);
      } finally {
        setIsCreateTaxCertificateLoading(false);
      }
    },
    [dispatch, snackbar]
  );

  const fetchTaxCertificateList = async (
    params: GetTaxCertificateListPayload
  ) => {
    try {
      const response = unwrapResult(
        await dispatch(taxCertificateAction.taxCertificateListThunk(params))
      );

      const urlParams = formatTaxCertificateListParams(params);

      if (
        response?.success &&
        response?.originalData?.data &&
        response?.originalData?.data.length <= 0
      ) {
        snackbar.show({
          severity: 'info',
          message: 'No tax certificate found'
        });
        // Reset to page 1 if not already page 1
        if (params.page !== 1) {
          const newParams = { ...params, page: 1 };
          const newUrlParams = formatTaxCertificateListParams(newParams);
          navigate(`/app/tax-certification?${newUrlParams.toString()}`);
          return; // Prevent further navigation with old params
        }
      }
      if (response?.success) {
        navigate(`/app/tax-certification?${urlParams.toString()}`);
      }
    } catch (error) {
      console.error('Error fetching tax certificate list:', error);
    }
  };

  const fetchTaxCertificateDetails = async (id: number): Promise<boolean> => {
    try {
      await dispatch(
        taxCertificateAction.taxCertificateDetailsThunk(id)
      ).unwrap();

      return true;
    } catch (error) {
      console.error('Error fetching tax certificate details:', error);
      return false;
    }
  };

  const setTaxCertificateListParams = async (
    params: GetTaxCertificateListPayload
  ) => {
    try {
      dispatch(taxCertificateAction.taxCertificateFilterParams(params));
    } catch (error) {
      console.error('Error fetching tax certificate list:', error);
    }
  };

  return {
    isCreateTaxCertificateLoading,
    enablePDF,
    setEnablePDF,
    handleCreateTaxCertificate,
    isLoading,
    taxCertificateList,
    taxCertificateListColumn,
    taxCertificateListParams,
    fetchTaxCertificateList,
    setTaxCertificateListParams,
    taxCertificateListMeta,
    fetchTaxCertificateDetails,
    taxCertificateDetails,
    handleUpdateTaxCertificate
  };
};
